/**
 * Account System Types (Schema V3)
 *
 * Scalable multi-role account system with relationship modeling.
 * Supports: learner, parent, teacher accounts with flexible linking.
 */

export type AccountRole = 'learner' | 'parent' | 'teacher' | 'admin'

/**
 * Base Account Interface
 * Common fields for all account types.
 */
interface BaseAccount {
  id: string // Stable UUID
  role: AccountRole
  displayName: string
  email?: string
  createdAt: number
  lastActive: number
}

/**
 * Learner Account
 * Student profile with learning data.
 */
export interface LearnerAccount extends BaseAccount {
  role: 'learner'
  grade?: number // Optional: current grade level
  birthYear?: number // Optional: for age-appropriate content
}

/**
 * Parent Account
 * Can view/manage multiple learner accounts.
 */
export interface ParentAccount extends BaseAccount {
  role: 'parent'
  email?: string
  phoneNumber?: string
}

/**
 * Teacher Account
 * Can view multiple learner accounts (classroom management).
 */
export interface TeacherAccount extends BaseAccount {
  role: 'teacher'
  email?: string
  schoolName?: string
  classroomName?: string
}

/**
 * Admin Account
 * Full access to governance, developer tools, and account management.
 */
export interface AdminAccount extends BaseAccount {
  role: 'admin'
  email?: string
}

/**
 * Account Union Type
 */
export type Account =
  | LearnerAccount
  | ParentAccount
  | TeacherAccount
  | AdminAccount

/**
 * Relationship Graph
 * Bidirectional mappings for account relationships.
 * Designed for efficient lookups in both directions.
 */
export interface RelationshipGraph {
  // Parent → Learners (1:N)
  parentToLearners: Record<string, string[]> // parentId → learnerIds[]

  // Learner → Parents (N:N - multiple parents can link to same child)
  learnerToParents: Record<string, string[]> // learnerId → parentIds[]

  // Teacher → Learners (1:N - classroom)
  teacherToLearners: Record<string, string[]> // teacherId → learnerIds[]

  // Future: Learner → Teachers (for multi-classroom scenarios)
  // learnerToTeachers: Record<string, string[]>
}

/**
 * Accounts State (replaces ProfilesState)
 * Top-level container for all accounts and relationships.
 */
export interface AccountsState {
  accountsById: Record<string, Account>
  activeAccountId: string // Current active account (any role)
  relationships: RelationshipGraph
}

/**
 * Active Context
 * Tracks which account is active and what they're viewing.
 */
export interface ActiveContext {
  accountId: string
  accountRole: AccountRole

  // For parent/teacher: which learner are they currently viewing?
  viewingLearnerId?: string
}

/**
 * Account Permissions
 * Defines what each role can do.
 */
export interface AccountPermissions {
  canEditLearnerData: boolean
  canViewLearnerData: boolean
  canManageAccounts: boolean
  canExportData: boolean
}

/**
 * Helper type guards
 */
export function isLearnerAccount(account: Account): account is LearnerAccount {
  return account.role === 'learner'
}
export function isParentAccount(account: Account): account is ParentAccount {
  return account.role === 'parent'
}
export function isTeacherAccount(account: Account): account is TeacherAccount {
  return account.role === 'teacher'
}
export function isAdminAccount(account: Account): account is AdminAccount {
  return account.role === 'admin'
}

/**
 * Get permissions for an account role
 */
export function getAccountPermissions(role: AccountRole): AccountPermissions {
  switch (role) {
    case 'learner':
      return {
        canEditLearnerData: true,
        canViewLearnerData: true,
        canManageAccounts: false,
        canExportData: false,
      }
    case 'parent':
      return {
        canEditLearnerData: false,
        canViewLearnerData: true,
        canManageAccounts: true,
        canExportData: true,
      }
    case 'teacher':
      return {
        canEditLearnerData: false,
        canViewLearnerData: true,
        canManageAccounts: false,
        canExportData: true,
      }
    case 'admin':
      return {
        canEditLearnerData: true,
        canViewLearnerData: true,
        canManageAccounts: true,
        canExportData: true,
      }
  }
}
